import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { IconSearch, IconX, IconUserPlus, IconLoader2, IconChevronDown, IconFilter } from '@tabler/icons-react';
import { apiClient } from '../../../../../config/apiConfig';
import CreatorRow from './CreatorRow';
import ScoreBar from './ScoreBar';
import GroupDropdown from './GroupDropdown';
import ExcludeCreatorsMenu from './ExcludeCreatorsMenu';
import { getCreatorDisplayName, getCreatorInitials } from '../../../../../utils/creatorHelpers';

const CreatorTable = ({
 creators,
 groups,
 selectedCreators,
 onSelectionChange,
 onGroupClick,
 activeDropdownCreatorId,
 onGroupAssign,
 onUnassignCreator,
 onCreateNewForCreator,
 onCloseDropdown,
 onAddCreator
}) => {
 const { campaignId } = useParams();
 const [excludedCampaigns, setExcludedCampaigns] = useState([]);
 const [excludedCreatorIds, setExcludedCreatorIds] = useState([]);
 const [excludeMenuOpen, setExcludeMenuOpen] = useState(false);
 const excludeButtonRef = useRef(null);

 // Resolve excluded creator IDs from the chosen campaigns. Mirrors the legacy
 // ExcludeCreatorsMenu predicate: only ONBOARDED/briefing_accepted creators
 // from other campaigns are filtered out — RE-onboardable creators stay visible.
 useEffect(() => {
 let cancelled = false;
 const fetchExcluded = async () => {
 if (excludedCampaigns.length === 0) {
 setExcludedCreatorIds([]);
 return;
 }
 try {
 const response = await apiClient.get('/campaigns', {
	 params: {
	 _id: excludedCampaigns,
	 status: 'ACTIVE',
	 includeCreators: 'true',
	 creatorProjection: 'statusOnly'
	 }
	 });
 const list = Array.isArray(response.data) ? response.data : (response.data?.campaigns || []);
 const ids = new Set();
 list.forEach(c => {
 (c.creators || []).forEach(creator => {
 if (creator.briefingStatus !== 'ONBOARDED' && creator.briefingStatus !== 'briefing_accepted') return;
 const raw = creator.creatorId;
 let id = null;
 if (raw && typeof raw === 'object') id = raw._id || raw;
 else if (typeof raw === 'string') id = raw;
 else if (creator._id) id = creator._id;
 if (id) ids.add(id.toString());
 });
 });
 if (!cancelled) setExcludedCreatorIds(Array.from(ids));
 } catch (err) {
 console.error('Error resolving excluded creators:', err);
 if (!cancelled) setExcludedCreatorIds([]);
 }
 };
 fetchExcluded();
 return () => { cancelled = true; };
 }, [excludedCampaigns]);
 const [sortBy, setSortBy] = useState('score');
 const [sortDesc, setSortDesc] = useState(true);
 const [searchQuery, setSearchQuery] = useState('');
 const [showAddForm, setShowAddForm] = useState(false);
 const [addHandle, setAddHandle] = useState('');
 const [adding, setAdding] = useState(false);
 const [suggestions, setSuggestions] = useState([]);
 const [searchingCreators, setSearchingCreators] = useState(false);
 const addInputRef = useRef(null);
 const suggestionsRef = useRef(null);
 const searchVersionRef = useRef(0);

 // De-duplicate creators by creatorId for pool display
 const uniqueCreators = useMemo(() => {
 const map = new Map();
 creators.forEach(c => {
 const cId = c.creatorId || c._id;
 if (!map.has(cId)) {
 map.set(cId, { ...c, creatorId: cId });
 }
 });
 return Array.from(map.values());
 }, [creators]);

 // Filter by search query + onboarded-elsewhere exclusion
 const filteredCreators = useMemo(() => {
 let list = uniqueCreators;
 if (excludedCreatorIds.length > 0) {
 const exclude = new Set(excludedCreatorIds);
 list = list.filter(c => !exclude.has((c.creatorId || c._id || '').toString()));
 }
 if (!searchQuery.trim()) return list;
 const q = searchQuery.toLowerCase().trim();
 return list.filter(c =>
 (c.name && c.name.toLowerCase().includes(q)) ||
 (c.twitterHandle && c.twitterHandle.toLowerCase().includes(q)) ||
 (c.discordUsername && c.discordUsername.toLowerCase().includes(q))
 );
 }, [uniqueCreators, searchQuery, excludedCreatorIds]);

 const allSelected = filteredCreators.length > 0 &&
 filteredCreators.every(c => selectedCreators.includes(c.creatorId || c._id));

 const handleSelectAll = () => {
 if (allSelected) {
 // Deselect only the filtered ones
 const filteredIds = new Set(filteredCreators.map(c => c.creatorId || c._id));
 onSelectionChange(selectedCreators.filter(id => !filteredIds.has(id)));
 } else {
 // Add filtered ones to selection (keep existing selections)
 const existing = new Set(selectedCreators);
 const newIds = filteredCreators.map(c => c.creatorId || c._id).filter(id => !existing.has(id));
 onSelectionChange([...selectedCreators, ...newIds]);
 }
 };

 const handleSort = (field) => {
 if (sortBy === field) {
 setSortDesc(!sortDesc);
 } else {
 setSortBy(field);
 setSortDesc(true);
 }
 };

 const sortedCreators = useMemo(() => {
 return [...filteredCreators].sort((a, b) => {
 let aVal, bVal;
 switch (sortBy) {
 case 'score':
 aVal = a.advancedMetrics?.score || 0;
 bVal = b.advancedMetrics?.score || 0;
 break;
 case 'smartFollowers':
 aVal = a.advancedMetrics?.smartFollowersCount || 0;
 bVal = b.advancedMetrics?.smartFollowersCount || 0;
 break;
 case 'impressions':
 aVal = a.advancedMetrics?.impressionReach || 0;
 bVal = b.advancedMetrics?.impressionReach || 0;
 break;
 case 'status':
 aVal = a.status || '';
 bVal = b.status || '';
 return sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
 case 'lastWorked':
 aVal = a.lastPaymentDate ? new Date(a.lastPaymentDate).getTime() : 0;
 bVal = b.lastPaymentDate ? new Date(b.lastPaymentDate).getTime() : 0;
 break;
 case 'name':
 aVal = a.name?.toLowerCase() || '';
 bVal = b.name?.toLowerCase() || '';
 return sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
 default:
 aVal = a.advancedMetrics?.score || 0;
 bVal = b.advancedMetrics?.score || 0;
 }
 return sortDesc ? bVal - aVal : aVal - bVal;
 });
 }, [filteredCreators, sortBy, sortDesc]);

 useEffect(() => {
 if (showAddForm && addInputRef.current) {
 addInputRef.current.focus();
 }
 if (!showAddForm) {
 setSuggestions([]);
 }
 }, [showAddForm]);

 // Debounced search for creator suggestions (with AbortController to prevent stale responses)
 useEffect(() => {
 if (!showAddForm || addHandle.length < 2) {
 setSuggestions([]);
 return;
 }

 const currentVersion = ++searchVersionRef.current;
 setSearchingCreators(true);
 const controller = new AbortController();

 const timeoutId = setTimeout(async () => {
 try {
 const response = await apiClient.get('/messages/search', {
 params: { query: addHandle, type: 'creator', projection: 'MINIMAL' },
 signal: controller.signal
 });
 // Only update if this is still the latest search (prevents stale results from shifting the list)
 if (searchVersionRef.current === currentVersion) {
 setSuggestions(response.data || []);
 }
 } catch (err) {
 if (err.name !== 'AbortError' && searchVersionRef.current === currentVersion) {
 setSuggestions([]);
 }
 } finally {
 if (searchVersionRef.current === currentVersion) {
 setSearchingCreators(false);
 }
 }
 }, 400);

 return () => {
 clearTimeout(timeoutId);
 controller.abort();
 setSearchingCreators(false);
 };
 }, [addHandle, showAddForm]);

 // Close suggestions on click outside
 useEffect(() => {
 if (suggestions.length === 0) return;
 const handleClickOutside = (e) => {
 if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
 addInputRef.current && !addInputRef.current.contains(e.target)) {
 setSuggestions([]);
 }
 };
 document.addEventListener('mousedown', handleClickOutside);
 return () => document.removeEventListener('mousedown', handleClickOutside);
 }, [suggestions.length]);

 const handleSelectSuggestion = useCallback(async (creator) => {
 const handle = creator.twitter?.username;
 if (!handle || !onAddCreator) return;
 searchVersionRef.current++; // Invalidate any in-flight searches so stale results can't arrive
 setAddHandle(handle);
 setSuggestions([]);
 setAdding(true);
 try {
 await onAddCreator(handle);
 setAddHandle('');
 setShowAddForm(false);
 } finally {
 setAdding(false);
 }
 }, [onAddCreator]);

 const handleAddCreator = async () => {
 if (!addHandle.trim() || !onAddCreator) return;
 searchVersionRef.current++; // Invalidate any in-flight searches
 setSuggestions([]);
 setAdding(true);
 try {
 await onAddCreator(addHandle.trim());
 setAddHandle('');
 setShowAddForm(false);
 } finally {
 setAdding(false);
 }
 };

 const SortableHeader = ({ field, children }) => (
 <th
 className="px-3 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer select-none"
 onClick={() => handleSort(field)}
 >
 {children}
 {sortBy === field && (
 <span className="ml-1">{sortDesc ? '\u2193' : '\u2191'}</span>
 )}
 </th>
 );

 return (
 <div className="bg-surface-1 border border-glass-border mb-6">
 <div className="px-3 sm:px-6 py-4 border-b border-glass-border space-y-3">
 <div className="flex justify-between items-center gap-3">
 <h2 className="text-lg font-semibold text-text-primary truncate">
 Reacted Creators ({uniqueCreators.length})
 </h2>
 <div className="flex items-center gap-2 flex-shrink-0">
 {onAddCreator && (
 <button
 onClick={() => setShowAddForm(!showAddForm)}
 className="px-3 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-white flex items-center gap-1.5 min-h-[36px]"
 >
 <IconUserPlus size={16} />
 <span className="hidden sm:inline">Add Creator</span>
 </button>
 )}
 <button
 onClick={handleSelectAll}
 className="px-3 sm:px-4 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-white min-h-[36px]"
 >
 {allSelected && selectedCreators.length > 0
 ? `Clear (${selectedCreators.length})`
 : `All (${filteredCreators.length})`}
 </button>
 </div>
 </div>
 <div className="flex items-center gap-2 flex-wrap">
 <div className="relative flex-1 sm:flex-initial">
 <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
 <input
 type="text"
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 placeholder="Search creators..."
 className="pl-9 pr-8 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-accent focus:border-transparent w-full sm:w-56"
 />
 {searchQuery && (
 <button
 onClick={() => setSearchQuery('')}
 className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
 >
 <IconX size={14} />
 </button>
 )}
 </div>
 <button
 ref={excludeButtonRef}
 type="button"
 onClick={() => setExcludeMenuOpen(o => !o)}
 className={`px-3 py-2 text-sm border flex items-center gap-1.5 min-h-[36px] ${
 excludedCampaigns.length > 0
 ? 'border-accent bg-accent-muted text-accent'
 : 'border-glass-border text-text-primary hover:bg-glass-bg-hover'
 }`}
 title="Exclude creators already onboarded into other campaigns"
 >
 <IconFilter size={14} />
 <span>Exclude{excludedCampaigns.length > 0 ? ` (${excludedCampaigns.length})` : ''}</span>
 </button>
 {excludedCreatorIds.length > 0 && (
 <span className="text-xs text-text-secondary">
 Hiding {excludedCreatorIds.length} onboarded elsewhere
 </span>
 )}
 </div>
 </div>

 <ExcludeCreatorsMenu
 anchorRef={excludeButtonRef}
 open={excludeMenuOpen}
 onClose={() => setExcludeMenuOpen(false)}
 onApply={(ids) => setExcludedCampaigns(ids)}
 initialSelectedCampaigns={excludedCampaigns}
 hideCampaignId={campaignId}
 />

 {showAddForm && onAddCreator && (
 <div className="ui-motion-enter px-3 sm:px-6 py-3 bg-surface-0 border-b border-glass-border">
 <div className="flex items-center gap-2 sm:gap-3">
 <span className="text-sm text-text-tertiary">@</span>
 <div className="relative flex-1 sm:max-w-xs">
 <input
 ref={addInputRef}
 type="text"
 value={addHandle}
 onChange={(e) => setAddHandle(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === 'Enter') handleAddCreator();
 if (e.key === 'Escape') setSuggestions([]);
 }}
 placeholder="Search by name or handle..."
 disabled={adding}
 autoComplete="off"
 className="w-full px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50"
 />
 {searchingCreators && (
 <IconLoader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary animate-spin" />
 )}

 {/* Suggestions dropdown */}
 {suggestions.length > 0 && !adding && (
 <div
 ref={suggestionsRef}
 className="ui-motion-menu absolute z-50 left-0 right-0 mt-1 bg-surface-1 border border-glass-border max-h-60 overflow-y-auto"
 >
 {suggestions.map((creator) => (
 <button
 key={creator._id}
 onClick={() => handleSelectSuggestion(creator)}
 className="w-full px-3 py-2 flex items-center gap-3 hover:bg-glass-bg-hover text-left"
 >
 {creator.pfp || creator.twitter?.profileImageUrl ? (
 <img
 src={creator.pfp || creator.twitter?.profileImageUrl}
 alt=""
 className="w-8 h-8 object-cover flex-shrink-0"
 onError={(e) => { e.target.style.display = 'none'; }}
 />
 ) : (
 <div className="w-8 h-8 bg-surface-3 flex items-center justify-center text-xs font-medium text-text-secondary flex-shrink-0">
 {getCreatorInitials(creator)}
 </div>
 )}
 <div className="min-w-0 flex-1">
 <div className="text-sm font-medium text-text-primary truncate">
 {getCreatorDisplayName(creator)}
 </div>
 {creator.twitter?.username && (
 <div className="text-xs text-text-secondary truncate">
 {creator.name || `@${creator.twitter.username}`}
 </div>
 )}
 </div>
 {creator.advancedMetrics?.score != null && (
 <span className="text-xs font-medium px-1.5 py-0.5 bg-accent-muted text-accent flex-shrink-0">
 {creator.advancedMetrics.score}
 </span>
 )}
 </button>
 ))}
 </div>
 )}

 {/* No results message */}
 {addHandle.length >= 2 && !searchingCreators && suggestions.length === 0 && !adding && (
 <div className="absolute z-50 left-0 right-0 mt-1 bg-surface-1 border border-glass-border px-3 py-2 text-xs text-text-secondary">
 No creators found. Press Enter to add by handle.
 </div>
 )}
 </div>
 <button
 onClick={handleAddCreator}
 disabled={adding || !addHandle.trim()}
 className="px-4 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-white disabled:opacity-50 flex items-center gap-2"
 >
 {adding && <IconLoader2 size={14} className="animate-spin" />}
 {adding ? 'Adding...' : 'Add'}
 </button>
 <button
 onClick={() => { setShowAddForm(false); setAddHandle(''); }}
 className="text-text-tertiary hover:text-text-primary"
 >
 <IconX size={16} />
 </button>
 </div>
 </div>
 )}

 {/* Mobile card view */}
 <div className="sm:hidden divide-y divide-glass-border">
 {sortedCreators.map(creator => {
 const cId = creator.creatorId || creator._id;
 const isSelected = selectedCreators.includes(cId);
 const creatorGroupIds = creators
 .filter(c => (c.creatorId || c._id) === cId && c.groupId)
 .map(c => c.groupId);
 const groupCount = creatorGroupIds.length;
 let groupLabel = groupCount === 0 ? 'Not assigned' : groupCount === 1 ? (groups.find(g => g._id === creatorGroupIds[0])?.name || '1 group') : `${groupCount} groups`;

 return (
 <div key={cId} className={`p-3 ${isSelected ? 'bg-accent-muted' : ''}`}>
 <div className="flex items-start gap-3">
 <input
 type="checkbox"
 checked={isSelected}
 onChange={(e) => {
 if (e.target.checked) onSelectionChange([...selectedCreators, cId]);
 else onSelectionChange(selectedCreators.filter(sid => sid !== cId));
 }}
 className=" border-glass-border cursor-pointer text-accent focus:ring-accent mt-1"
 />
 {creator.profileImage ? (
 <img src={creator.profileImage} alt="" className="w-8 h-8 object-cover flex-shrink-0" />
 ) : (
 <div className="w-8 h-8 bg-accent-muted flex items-center justify-center text-xs font-medium text-accent flex-shrink-0">
 {getCreatorInitials(creator)}
 </div>
 )}
 <div className="flex-1 min-w-0">
 <div className="flex items-center justify-between gap-2">
 <div className="min-w-0">
 <div className="text-sm font-medium text-text-primary truncate">{getCreatorDisplayName(creator)}</div>
 {creator.name && <div className="text-xs text-text-secondary truncate">{creator.name}</div>}
 </div>
 <ScoreBar score={creator.advancedMetrics?.score || 0} />
 </div>
 <div className="flex items-center justify-between gap-2 mt-2">
 <div className="flex items-center gap-2 text-xs text-text-secondary">
 <span>{(creator.advancedMetrics?.smartFollowersCount || 0).toLocaleString()} SF</span>
 <span className="text-text-tertiary">&middot;</span>
 <span>{(creator.advancedMetrics?.impressionReach || 0).toLocaleString()} impr</span>
 </div>
 <button
 onClick={() => onGroupClick(cId)}
 className={`inline-flex items-center px-2 py-1 text-xs border ${
 groupCount > 0
 ? 'border-accent/30 bg-accent-muted text-accent hover:bg-accent-muted/80'
 : 'border-glass-border bg-surface-2 text-text-secondary hover:bg-glass-bg-hover'
 }`}
 >
 <span className="truncate max-w-[80px]">{groupLabel}</span>
 <IconChevronDown size={12} className="flex-shrink-0 ml-1 opacity-50" />
 </button>
 </div>
 </div>
 </div>
 {activeDropdownCreatorId === cId && (
 <div className="mt-2 ml-8">
 <GroupDropdown
 groups={groups}
 creators={creators}
 creatorGroupIds={creatorGroupIds}
 onSelect={(groupId) => onGroupAssign(cId, groupId)}
 onUnassign={(groupId) => onUnassignCreator(cId, groupId)}
 onCreateNew={() => onCreateNewForCreator(cId)}
 onClose={onCloseDropdown}
 inline
 />
 </div>
 )}
 </div>
 );
 })}
 </div>

 {/* Desktop table view */}
 <div className="hidden sm:block overflow-x-auto">
 <table className="w-full divide-y divide-glass-border table-fixed">
 <thead className="bg-surface-0">
 <tr>
 <th className="pl-3 pr-1 py-3 text-left w-10"></th>
 <SortableHeader field="name">Creator</SortableHeader>
 <SortableHeader field="score">Score</SortableHeader>
 <SortableHeader field="status">Status</SortableHeader>
 <SortableHeader field="smartFollowers">Smart Foll.</SortableHeader>
 <SortableHeader field="impressions">Est. Impr.</SortableHeader>
 <SortableHeader field="lastWorked">Last Worked</SortableHeader>
 <th className="px-3 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Group</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-glass-border">
 {sortedCreators.map(creator => {
 const cId = creator.creatorId || creator._id;
 return (
 <CreatorRow
 key={cId}
 creator={creator}
 isSelected={selectedCreators.includes(cId)}
 bulkCount={selectedCreators.length > 1 && selectedCreators.includes(cId) ? selectedCreators.length : 0}
 onSelect={(id, checked) => {
 if (checked) {
 onSelectionChange([...selectedCreators, id]);
 } else {
 onSelectionChange(selectedCreators.filter(sid => sid !== id));
 }
 }}
 onGroupClick={onGroupClick}
 groups={groups}
 showDropdown={activeDropdownCreatorId === cId}
 onGroupAssign={onGroupAssign}
 onUnassignCreator={onUnassignCreator}
 onCreateNewForCreator={onCreateNewForCreator}
 onCloseDropdown={onCloseDropdown}
 creators={creators}
 />
 );
 })}
 </tbody>
 </table>
 </div>

 {sortedCreators.length === 0 && (
 <div className="text-center py-12 text-text-secondary">
 {searchQuery
 ? `No creators matching "${searchQuery}"`
 : 'No creators yet. Creators will appear here once they react to the Discord announcement.'}
 </div>
 )}
 </div>
 );
};

export default CreatorTable;
